import React from 'react';
import './SiderResizeHandle.less';

export const DEFAULT_SIDER_WIDTH = 208;
export const MIN_SIDER_WIDTH = 180;
export const MAX_SIDER_WIDTH = 260;
export const SIDER_WIDTH_STORAGE_KEY = 'easy-record:sider-width:v2';
export const clampSiderWidth = (width: number) =>
  Math.min(MAX_SIDER_WIDTH, Math.max(MIN_SIDER_WIDTH, width));

export function getStoredSiderWidth() {
  const stored =
    typeof window !== 'undefined'
      ? localStorage.getItem(SIDER_WIDTH_STORAGE_KEY)
      : null;
  const width = stored ? Number(stored) : DEFAULT_SIDER_WIDTH;
  return Number.isFinite(width) ? clampSiderWidth(width) : DEFAULT_SIDER_WIDTH;
}

export default function SiderResizeHandle({
  enabled,
  siderWidth,
  onResizeWidth,
}: {
  enabled: boolean;
  siderWidth: number;
  onResizeWidth: (width: number) => void;
}) {
  const [geometry, setGeometry] = React.useState({
    left: siderWidth,
    bottom: 0,
  });
  const [dragging, setDragging] = React.useState(false);
  const pointer = React.useRef<{ id: number; x: number; width: number } | null>(
    null,
  );
  React.useEffect(() => {
    if (!enabled) return;
    const sider = document.querySelector<HTMLElement>('.ant-pro-sider');
    if (!sider) return;
    const update = () => {
      const rect = sider.getBoundingClientRect();
      const collapse = sider.querySelector<HTMLElement>(
        '.ant-pro-sider-collapsed-button',
      );
      const bottom = collapse
        ? Math.max(
            0,
            window.innerHeight - collapse.getBoundingClientRect().top + 12,
          )
        : 0;
      setGeometry({ left: rect.right, bottom });
    };
    const observer = new ResizeObserver(update);
    observer.observe(sider);
    update();
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [enabled, siderWidth]);
  const resize = (width: number) => {
    const next = clampSiderWidth(width);
    localStorage.setItem(SIDER_WIDTH_STORAGE_KEY, String(next));
    onResizeWidth(next);
  };
  if (!enabled) return null;
  return (
    <hr
      className={`shell-sider-resize${dragging ? ' is-dragging' : ''}`}
      style={{ left: geometry.left - 6, bottom: geometry.bottom }}
      tabIndex={0}
      aria-label="调整侧栏宽度"
      aria-orientation="vertical"
      aria-valuemin={MIN_SIDER_WIDTH}
      aria-valuemax={MAX_SIDER_WIDTH}
      aria-valuenow={siderWidth}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        pointer.current = {
          id: event.pointerId,
          x: event.clientX,
          width: siderWidth,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(true);
      }}
      onPointerMove={(event) => {
        const start = pointer.current;
        if (start?.id === event.pointerId)
          resize(start.width + event.clientX - start.x);
      }}
      onLostPointerCapture={() => {
        pointer.current = null;
        setDragging(false);
      }}
      onPointerUp={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId))
          event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onKeyDown={(event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        resize(
          siderWidth +
            (event.key === 'ArrowLeft' ? -1 : 1) * (event.shiftKey ? 24 : 8),
        );
      }}
    />
  );
}
